import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { resolveMostRecentContext } from '../utils/mostRecentContext';

/**
 * Hook to get the current story ID, fetching it from the backend if not
 * available in AppContext. This is useful for pages that need a story ID
 * but may render before AppContext has loaded the full context.
 *
 * Returns { storyId, isLoading, error }
 */
export function useStoryId() {
  const { currentStory } = useApp();
  const [storyId, setStoryId] = useState(currentStory?.id || null);
  const [isLoading, setIsLoading] = useState(!currentStory?.id);
  const [error, setError] = useState(null);

  useEffect(() => {
    // If we have currentStory from context, use it
    if (currentStory?.id) {
      setStoryId(currentStory.id);
      setIsLoading(false);
      return;
    }

    // Otherwise, fetch the most recent story from the backend
    let cancelled = false;
    const fetchStoryId = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const { story } = await resolveMostRecentContext();
        if (cancelled) return;
        if (story) setStoryId(story.id);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch story ID:', err);
        setError(err.message || 'Failed to load story');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchStoryId();
    return () => { cancelled = true; };
  }, [currentStory?.id]);

  return { storyId, isLoading, error };
}

export default useStoryId;
